import { useEffect, useMemo, useState } from 'react'
import {
	Alert,
	Box,
	Button,
	Card,
	CardContent,
	Checkbox,
	Chip,
	CircularProgress,
	Collapse,
	Dialog,
	DialogActions,
	DialogContent,
	DialogTitle,
	Divider,
	FormControlLabel,
	IconButton,
	MenuItem,
	Select,
	Stack,
	TextField,
	Tooltip,
	Typography,
} from '@mui/material'
import { Add, CheckCircleOutline, Close, ContentCopy as ContentCopyIcon, Delete, Edit, ExpandLess, SwapHoriz } from '@mui/icons-material'
import { DeleteConfirmationDialog } from '../DeleteConfirmationDialog'
import { StatusChip } from './StatusChip'

function InlineCustomerEditPanel({ customer, onSave, onCancel, loading }) {
	const initialUrl = customer.webhook_url || ''
	const [form, setForm] = useState({
		name: customer.name || '',
		webhook_url: initialUrl,
		webhook_api_key: customer.webhook_api_key || '',
		webhook_enabled: initialUrl.trim() ? (customer.webhook_enabled ?? false) : false,
		show_branding: customer.show_branding ?? true,
		custom_logo: customer.custom_logo || '',
	})

	const handleSubmit = (e) => {
		e.preventDefault()
		onSave(form)
	}

	return (
		<Box component="form" onSubmit={handleSubmit} sx={{ px: 3, py: 2.5 }}>
			<Stack spacing={2.5}>
				<Box>
					<Typography variant="overline" color="text.secondary" display="block" sx={{ mb: 1.5, lineHeight: 1 }}>
						Account Details
					</Typography>
					<TextField
						fullWidth
						label="Customer Name"
						value={form.name}
						onChange={(e) => setForm({ ...form, name: e.target.value })}
						required
						size="small"
					/>
				</Box>

				<Divider />

				<Box>
					<Typography variant="overline" color="text.secondary" display="block" sx={{ mb: 1.5, lineHeight: 1 }}>
						Webhook Configuration
					</Typography>
					<Stack spacing={1.5}>
						<TextField
							fullWidth
							label="Webhook URL"
							type="url"
							value={form.webhook_url}
							onChange={(e) => {
								const next = e.target.value
								setForm((prev) => ({
									...prev,
									webhook_url: next,
									webhook_enabled: next.trim() ? prev.webhook_enabled : false,
								}))
							}}
							size="small"
						/>
						<TextField
							fullWidth
							label="Webhook API Key"
							value={form.webhook_api_key}
							onChange={(e) => setForm({ ...form, webhook_api_key: e.target.value })}
							size="small"
						/>
						<FormControlLabel
							control={
								<Checkbox
									size="small"
									checked={form.webhook_enabled}
									disabled={!form.webhook_url.trim()}
									onChange={(e) => setForm({ ...form, webhook_enabled: e.target.checked })}
								/>
							}
							label={
								<Typography variant="body2" color={form.webhook_url.trim() ? 'text.primary' : 'text.disabled'}>
									Enable webhooks{!form.webhook_url.trim() && ' (requires Webhook URL)'}
								</Typography>
							}
						/>
					</Stack>
				</Box>

				<Divider />

				<Box>
					<Typography variant="overline" color="text.secondary" display="block" sx={{ mb: 1, lineHeight: 1 }}>
						Branding
					</Typography>
					<FormControlLabel
						control={
							<Checkbox
								size="small"
								checked={form.show_branding}
								onChange={(e) => setForm({ ...form, show_branding: e.target.checked })}
							/>
						}
						label={<Typography variant="body2">Show Aquatic Labs branding</Typography>}
					/>
					<TextField
						fullWidth
						size="small"
						label="Custom logo filename"
						placeholder="e.g. omen.svg"
						value={form.custom_logo}
						onChange={(e) => setForm({ ...form, custom_logo: e.target.value })}
						helperText="Asset in frontend/src/assets/customer-logos/. Rendered next to the Aquatic Labs icon."
						sx={{ mt: 1.5 }}
					/>
				</Box>

				<Box display="flex" gap={1} justifyContent="flex-end">
					<Button size="small" variant="outlined" onClick={onCancel}>Cancel</Button>
					<Button size="small" variant="contained" type="submit" disabled={loading}>
						{loading ? 'Saving…' : 'Save Changes'}
					</Button>
				</Box>
			</Stack>
		</Box>
	)
}

export function CustomerDetailDialog({
	customerId,
	customers,
	open,
	onClose,
	onUpdateCustomer,
	onDelete,
	onToggleWebhook,
	onToggleSensorDelivery,
	onToggleSensorRealtimeEnabled,
	onToggleSensorApiEnabled,
	onUpdateSensorVisibility,
	onCreateSensor,
	onUpdateSensor,
	onDeleteSensor,
	onTransferSensor,
	operationLoading,
}) {
	const [editOpen, setEditOpen] = useState(false)
	const [editLoading, setEditLoading] = useState(false)
	const [editingSensorId, setEditingSensorId] = useState(null)
	const [sensorEditForm, setSensorEditForm] = useState({ sensor_id: '', sensor_location: '' })
	const [sensorEditLoading, setSensorEditLoading] = useState(false)
	const [addingSensor, setAddingSensor] = useState(false)
	const [newSensorForm, setNewSensorForm] = useState({ sensor_id: '', sensor_location: '' })
	const [addSensorLoading, setAddSensorLoading] = useState(false)
	const [addSensorError, setAddSensorError] = useState('')
	const [deleteSensorDialog, setDeleteSensorDialog] = useState({ open: false, id: null, name: '' })
	const [transferDialog, setTransferDialog] = useState({ open: false, sensor: null, targetCustomerId: '' })
	const [transferLoading, setTransferLoading] = useState(false)

	const customer = useMemo(
		() => customers.find(c => c.id === customerId) ?? null,
		[customers, customerId]
	)

	const allAssignedSensorIds = useMemo(() => {
		const ids = new Set()
		customers.forEach(c => {
			c.sensor_configs?.forEach(s => ids.add(s.sensor_id.trim().toLowerCase()))
		})
		return ids
	}, [customers])

	useEffect(() => {
		if (!open) {
			setEditOpen(false)
			setEditingSensorId(null)
			setAddingSensor(false)
			setNewSensorForm({ sensor_id: '', sensor_location: '' })
			setAddSensorError('')
		}
	}, [open])

	const startSensorEdit = (sensor) => {
		setEditingSensorId(sensor.id)
		setSensorEditForm({ sensor_id: sensor.sensor_id, sensor_location: sensor.sensor_location || '' })
	}

	const cancelSensorEdit = () => {
		setEditingSensorId(null)
		setSensorEditForm({ sensor_id: '', sensor_location: '' })
	}

	const handleSaveSensorEdit = async (sensorId) => {
		setSensorEditLoading(true)
		try {
			await onUpdateSensor(sensorId, sensorEditForm)
			setEditingSensorId(null)
		} finally {
			setSensorEditLoading(false)
		}
	}

	const handleAddSensor = async () => {
		const trimmedId = newSensorForm.sensor_id.trim()
		if (!trimmedId) return
		if (allAssignedSensorIds.has(trimmedId.toLowerCase())) {
			setAddSensorError('This sensor ID is already assigned to another customer')
			return
		}
		setAddSensorError('')
		setAddSensorLoading(true)
		try {
			await onCreateSensor(customer.id, newSensorForm)
			setAddingSensor(false)
			setNewSensorForm({ sensor_id: '', sensor_location: '' })
		} finally {
			setAddSensorLoading(false)
		}
	}

	const handleDeleteSensor = async () => {
		await onDeleteSensor(deleteSensorDialog.id)
		setDeleteSensorDialog({ open: false, id: null, name: '' })
	}

	const handleTransfer = async () => {
		if (!transferDialog.targetCustomerId) return
		setTransferLoading(true)
		try {
			await onTransferSensor(transferDialog.sensor.id, transferDialog.targetCustomerId)
			setTransferDialog({ open: false, sensor: null, targetCustomerId: '' })
		} finally {
			setTransferLoading(false)
		}
	}

	if (!customer) return null

	const handleSaveEdit = async (formData) => {
		setEditLoading(true)
		try {
			await onUpdateCustomer(customer.id, formData)
			setEditOpen(false)
		} finally {
			setEditLoading(false)
		}
	}

	return (
		<Dialog open={open} onClose={onClose} maxWidth="md" fullWidth scroll="paper">
			<DialogTitle sx={{ pb: 1 }}>
				<Box display="flex" justifyContent="space-between" alignItems="flex-start">
					<Box>
						<Typography variant="h6" fontWeight="bold">{customer.name}</Typography>
						<Typography variant="caption" color="text.secondary">Customer ID: {customer.id}</Typography>
					</Box>
					<Box display="flex" gap={1} alignItems="center">
						<Button
							size="small"
							variant={editOpen ? 'contained' : 'outlined'}
							startIcon={editOpen ? <ExpandLess /> : <Edit />}
							onClick={() => setEditOpen(v => !v)}
						>
							{editOpen ? 'Close Edit' : 'Edit'}
						</Button>
						<Button size="small" variant="outlined" color="error" startIcon={<Delete />} onClick={() => onDelete(customer.id, customer.name)}>
							Delete
						</Button>
						<IconButton size="small" onClick={onClose}>
							<Close />
						</IconButton>
					</Box>
				</Box>
			</DialogTitle>
			<Divider />

			<DialogContent sx={{ p: 0 }}>
				<Collapse in={editOpen}>
					<Box sx={{ bgcolor: 'action.hover', borderBottom: '1px solid', borderColor: 'divider' }}>
						<InlineCustomerEditPanel
							key={customer.updated_at}
							customer={customer}
							onSave={handleSaveEdit}
							onCancel={() => setEditOpen(false)}
							loading={editLoading}
						/>
					</Box>
				</Collapse>

				<Box sx={{ px: 3, py: 2, display: 'flex', gap: 1, flexWrap: 'wrap', borderBottom: '1px solid', borderColor: 'divider' }}>
					<StatusChip
						on={customer.webhook_enabled}
						onLabel="Webhooks ON"
						offLabel="Webhooks OFF"
						loading={operationLoading === `webhook-${customer.id}`}
						onClick={() => onToggleWebhook(customer.id)}
					/>
					<Chip
						label={customer.show_branding ? 'Branding visible' : 'Branding hidden'}
						size="small"
						variant="outlined"
					/>
				</Box>

				<Box sx={{ px: 3, py: 2.5, borderBottom: '1px solid', borderColor: 'divider' }}>
					<Typography variant="overline" color="text.secondary" display="block" gutterBottom>
						Webhook
					</Typography>
					<Typography variant="body2" sx={{ mb: 0.5 }}>
						<strong>URL:</strong>{' '}
						<code style={{ fontSize: '0.8rem' }}>{customer.webhook_url || 'Not set'}</code>
					</Typography>
					{customer.webhook_api_key && (
						<Box display="flex" alignItems="center" gap={1}>
							<Typography variant="body2">
								<strong>API Key:</strong>{' '}
								<code style={{ fontSize: '0.8rem' }}>****{customer.webhook_api_key.slice(-4)}</code>
							</Typography>
							<Tooltip title="Copy API key">
								<IconButton size="small" onClick={() => navigator.clipboard.writeText(customer.webhook_api_key)}>
									<ContentCopyIcon fontSize="small" />
								</IconButton>
							</Tooltip>
						</Box>
					)}
					<Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 1 }}>
						Last updated: {customer.updated_at ? new Date(customer.updated_at).toLocaleString() : 'N/A'}
					</Typography>
				</Box>

			<Box sx={{ px: 3, py: 2.5 }}>
				<Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
					<Typography variant="overline" color="text.secondary">
						Sensors ({customer.sensor_configs?.length || 0})
					</Typography>
					<Button
						size="small"
						startIcon={<Add />}
						onClick={() => {
							setAddingSensor(v => !v)
							setNewSensorForm({ sensor_id: '', sensor_location: '' })
						}}
					>
						Add Sensor
					</Button>
				</Box>

			<Collapse in={addingSensor}>
				<Card variant="outlined" sx={{ borderRadius: 2, mb: 1.5, bgcolor: 'action.hover' }}>
					<CardContent sx={{ py: 1.5, '&:last-child': { pb: 1.5 } }}>
						<Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 1, fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.05em' }}>
							New Sensor
						</Typography>
						<Box display="flex" gap={1} alignItems="center" flexWrap="wrap">
							<TextField
								size="small"
								label="Sensor ID"
								value={newSensorForm.sensor_id}
								onChange={(e) => {
									setNewSensorForm(f => ({ ...f, sensor_id: e.target.value }))
									if (addSensorError) setAddSensorError('')
								}}
								sx={{ flex: 1, minWidth: 140 }}
								autoFocus
								error={!!addSensorError}
							/>
							<TextField
								size="small"
								label="Location"
								value={newSensorForm.sensor_location}
								onChange={(e) => setNewSensorForm(f => ({ ...f, sensor_location: e.target.value }))}
								sx={{ flex: 1, minWidth: 140 }}
							/>
							<Tooltip title="Save sensor">
								<span>
									<IconButton
										size="small"
										color="primary"
										disabled={addSensorLoading || !newSensorForm.sensor_id.trim()}
										onClick={handleAddSensor}
									>
										{addSensorLoading ? <CircularProgress size={16} /> : <CheckCircleOutline fontSize="small" />}
									</IconButton>
								</span>
							</Tooltip>
							<Tooltip title="Cancel">
								<IconButton size="small" onClick={() => { setAddingSensor(false); setAddSensorError('') }}>
									<Close fontSize="small" />
								</IconButton>
							</Tooltip>
						</Box>
						{addSensorError && (
							<Alert severity="error" sx={{ mt: 1, py: 0, '& .MuiAlert-message': { py: 0.5 } }}>
								{addSensorError}
							</Alert>
						)}
					</CardContent>
				</Card>
			</Collapse>

				<Stack spacing={1.5}>
					{customer.sensor_configs?.map((sensor) => {
						const isEditing = editingSensorId === sensor.id
						return (
							<Card key={sensor.id} variant="outlined" sx={{ borderRadius: 2 }}>
								<CardContent sx={{ py: 1.5, '&:last-child': { pb: 1.5 } }}>
									<Box display="flex" justifyContent="space-between" alignItems="flex-start" mb={1}>
										{isEditing ? (
											<Box display="flex" gap={1} alignItems="center" flex={1} mr={1} flexWrap="wrap">
												<TextField
													size="small"
													label="Sensor ID"
													value={sensorEditForm.sensor_id}
													onChange={(e) => setSensorEditForm(f => ({ ...f, sensor_id: e.target.value }))}
													sx={{ flex: 1, minWidth: 120 }}
													autoFocus
												/>
												<TextField
													size="small"
													label="Location"
													value={sensorEditForm.sensor_location}
													onChange={(e) => setSensorEditForm(f => ({ ...f, sensor_location: e.target.value }))}
													sx={{ flex: 1, minWidth: 120 }}
												/>
											</Box>
										) : (
											<Box>
												<Typography variant="body2" fontWeight="bold">{sensor.sensor_id}</Typography>
												<Typography variant="caption" color="text.secondary">
													{sensor.sensor_location || 'No location'}
												</Typography>
											</Box>
										)}
										<Box display="flex" gap={0.5} flexShrink={0}>
											{isEditing ? (
												<>
													<Tooltip title="Save">
														<span>
															<IconButton
																size="small"
																color="primary"
																disabled={sensorEditLoading || !sensorEditForm.sensor_id.trim()}
																onClick={() => handleSaveSensorEdit(sensor.id)}
															>
																{sensorEditLoading ? <CircularProgress size={16} /> : <CheckCircleOutline fontSize="small" />}
															</IconButton>
														</span>
													</Tooltip>
													<Tooltip title="Cancel">
														<IconButton size="small" onClick={cancelSensorEdit}>
															<Close fontSize="small" />
														</IconButton>
													</Tooltip>
												</>
											) : (
											<>
												<Tooltip title="Edit sensor">
													<IconButton size="small" onClick={() => startSensorEdit(sensor)}>
														<Edit fontSize="small" />
													</IconButton>
												</Tooltip>
												<Tooltip title="Transfer to another customer">
													<IconButton size="small" onClick={() => setTransferDialog({ open: true, sensor, targetCustomerId: '' })}>
														<SwapHoriz fontSize="small" />
													</IconButton>
												</Tooltip>
												<Tooltip title="Delete sensor">
													<IconButton
														size="small"
														color="error"
														onClick={() => setDeleteSensorDialog({ open: true, id: sensor.id, name: sensor.sensor_id })}
													>
														<Delete fontSize="small" />
													</IconButton>
												</Tooltip>
											</>
										)}
										</Box>
									</Box>

									<Box display="flex" gap={0.75} flexWrap="wrap" mb={1}>
										<Tooltip title="Master control — when OFF, sensor is completely disabled">
											<span>
												<StatusChip
													on={sensor.delivery_enabled}
													onLabel="Delivery ON"
													offLabel="Delivery OFF"
													loading={operationLoading === `sensor-${customer.id}-${sensor.sensor_id}`}
													onClick={() => onToggleSensorDelivery(customer.id, sensor.sensor_id)}
												/>
											</span>
										</Tooltip>
										<Tooltip title="Websocket broadcasts — when OFF, no live dashboard updates">
											<span>
												<StatusChip
													on={sensor.realtime_enabled}
													onLabel="Realtime ON"
													offLabel="Realtime OFF"
													loading={operationLoading === `realtime-${sensor.id}`}
													onClick={() => onToggleSensorRealtimeEnabled(sensor.id)}
												/>
											</span>
										</Tooltip>
										<Tooltip title="API access — when OFF, customer API calls return 403">
											<span>
												<StatusChip
													on={sensor.api_fetch_enabled}
													onLabel="API ON"
													offLabel="API OFF"
													loading={operationLoading === `api-${sensor.id}`}
													onClick={() => onToggleSensorApiEnabled(sensor.id)}
												/>
											</span>
										</Tooltip>
									</Box>

									<Box display="flex" flexWrap="wrap">
										{[
											{ field: 'show_ph', label: 'pH' },
											{ field: 'show_alkalinity', label: 'Alkalinity' },
											{ field: 'show_conductivity', label: 'Conductivity' },
											{ field: 'show_temperature', label: 'Temperature' },
										].map(({ field, label }) => (
											<FormControlLabel
												key={field}
												control={
													<Checkbox
														size="small"
														checked={!!sensor[field]}
														onChange={(e) => onUpdateSensorVisibility(sensor.id, {
															show_ph: field === 'show_ph' ? e.target.checked : !!sensor.show_ph,
															show_alkalinity: field === 'show_alkalinity' ? e.target.checked : !!sensor.show_alkalinity,
															show_conductivity: field === 'show_conductivity' ? e.target.checked : !!sensor.show_conductivity,
															show_temperature: field === 'show_temperature' ? e.target.checked : !!sensor.show_temperature,
														})}
													/>
												}
												label={<Typography variant="caption">{label}</Typography>}
												sx={{ mr: 0.5 }}
											/>
										))}
									</Box>
								</CardContent>
							</Card>
						)
					})}
					{!customer.sensor_configs?.length && !addingSensor && (
						<Typography variant="body2" color="text.secondary">No sensors configured.</Typography>
					)}
				</Stack>
			</Box>
			</DialogContent>

		<Divider />
		<DialogActions sx={{ px: 3, py: 1.5 }}>
			<Button onClick={onClose} variant="outlined" size="small">Close</Button>
		</DialogActions>

	<DeleteConfirmationDialog
		open={deleteSensorDialog.open}
		onClose={() => setDeleteSensorDialog({ open: false, id: null, name: '' })}
		onConfirm={handleDeleteSensor}
		title="Delete Sensor"
		itemName={deleteSensorDialog.name}
		itemType="sensor"
	/>

	<Dialog open={transferDialog.open} onClose={() => setTransferDialog({ open: false, sensor: null, targetCustomerId: '' })} maxWidth="sm" fullWidth>
		<DialogTitle>
			<Box display="flex" justifyContent="space-between" alignItems="center">
				<Typography variant="h6" fontWeight="bold">Transfer Sensor</Typography>
				<IconButton size="small" onClick={() => setTransferDialog({ open: false, sensor: null, targetCustomerId: '' })}><Close /></IconButton>
			</Box>
		</DialogTitle>
		<Divider />
		<DialogContent sx={{ pt: 3 }}>
			<Alert severity="info" sx={{ mb: 3 }}>
				<Typography variant="body2" fontWeight="bold" gutterBottom>
					Future ownership transfer only
				</Typography>
				<Typography variant="body2">
					New data will be collected under the destination customer. Historical measurements,
					issues, and delivery history remain with <strong>{customer.name}</strong>.
				</Typography>
			</Alert>

			<Box sx={{ mb: 2 }}>
				<Typography variant="body2" color="text.secondary" gutterBottom>Sensor</Typography>
				<Typography variant="body1" fontWeight="bold">{transferDialog.sensor?.sensor_id}</Typography>
				{transferDialog.sensor?.sensor_location && (
					<Typography variant="body2" color="text.secondary">{transferDialog.sensor.sensor_location}</Typography>
				)}
			</Box>

			<Box sx={{ mb: 2 }}>
				<Typography variant="body2" color="text.secondary" gutterBottom>Current Customer</Typography>
				<Typography variant="body1">{customer.name}</Typography>
			</Box>

			<Select
				fullWidth
				displayEmpty
				value={transferDialog.targetCustomerId}
				onChange={(e) => setTransferDialog(d => ({ ...d, targetCustomerId: e.target.value }))}
				renderValue={(v) => v ? customers.find(c => String(c.id) === String(v))?.name : 'Select destination customer'}
			>
				<MenuItem value="" disabled>Select destination customer</MenuItem>
				{customers
					.filter(c => c.id !== customer.id)
					.map(c => (
						<MenuItem key={c.id} value={c.id}>{c.name}</MenuItem>
					))}
			</Select>
		</DialogContent>
		<Divider />
		<DialogActions sx={{ px: 3, py: 2 }}>
			<Button onClick={() => setTransferDialog({ open: false, sensor: null, targetCustomerId: '' })} variant="outlined">Cancel</Button>
			<Button
				variant="contained"
				startIcon={<SwapHoriz />}
				disabled={!transferDialog.targetCustomerId || transferLoading}
				onClick={handleTransfer}
			>
				{transferLoading ? 'Transferring…' : 'Transfer Sensor'}
			</Button>
		</DialogActions>
	</Dialog>
</Dialog>
)
}
